import React, { useEffect, useState } from "react";
import styled from "styled-components";

import Modal from "../../Modal";

import { AdminButton } from "../../../pages/admin/action_engine";
const DEFAULT_VALUES = Object.freeze({
  rule_type: "unit_discount",
  priority: 100,
  configuration: {},
  active: true,
});

function valuesFromRule(rule) {
  if (!rule) return DEFAULT_VALUES;
  return {
    rule_type: rule.rule_type || DEFAULT_VALUES.rule_type,
    priority: Number.isInteger(rule.priority) ? rule.priority : 100,
    configuration: rule.configuration && typeof rule.configuration === "object"
      && !Array.isArray(rule.configuration) ? rule.configuration : {},
    active: rule.active !== false,
  };
}

export default function CommercialRuleForm({
  mode,
  initialRule,
  submitting,
  errorMessage,
  onSubmit,
  onCancel,
}) {
  const initial = valuesFromRule(initialRule);
  const [ruleType, setRuleType] = useState(initial.rule_type);
  const [priority, setPriority] = useState(String(initial.priority));
  const [configurationText, setConfigurationText] = useState(
    JSON.stringify(initial.configuration, null, 2),
  );
  const [active, setActive] = useState(initial.active);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    const values = valuesFromRule(initialRule);
    setRuleType(values.rule_type);
    setPriority(String(values.priority));
    setConfigurationText(JSON.stringify(values.configuration, null, 2));
    setActive(values.active);
    setValidationError("");
  }, [initialRule, mode]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submitting) return;

    const parsedPriority = Number(priority);
    if (!Number.isInteger(parsedPriority) || parsedPriority < 0) {
      setValidationError("La priorité doit être un entier positif ou nul.");
      return;
    }

    let configuration;
    try {
      configuration = JSON.parse(configurationText);
    } catch (_error) {
      setValidationError("La configuration doit être un objet JSON valide.");
      return;
    }

    if (!configuration || typeof configuration !== "object" || Array.isArray(configuration)) {
      setValidationError("La configuration doit être un objet JSON.");
      return;
    }

    setValidationError("");
    onSubmit({
      rule_type: ruleType,
      priority: parsedPriority,
      configuration,
      active,
    });
  };

  return (
    <Modal
      onClose={submitting ? undefined : onCancel}
      role="dialog"
      ariaLabelledBy="commercial-rule-form-title"
    >
      <Form onSubmit={handleSubmit}>
        <h2 id="commercial-rule-form-title">
          {mode === "edit" ? "Modifier la règle commerciale" : "Créer une règle commerciale"}
        </h2>

        <Field>
          <label htmlFor="commercial-rule-type">Type de règle</label>
          <select
            id="commercial-rule-type"
            value={ruleType}
            disabled={submitting}
            onChange={(event) => setRuleType(event.target.value)}
          >
            <option value="unit_discount">Remise unitaire</option>
            <option value="category_discount">Remise par catégorie</option>
            <option value="case_pack_discount">Remise au colisage</option>
            <option value="until_case_pack_minus_one_discount">Remise jusqu’à colisage - 1</option>
            <option value="tier_discount">Remise par paliers</option>
            <option value="free_unit">Unités offertes</option>
          </select>
        </Field>

        <Field>
          <label htmlFor="commercial-rule-priority">Priorité</label>
          <input
            id="commercial-rule-priority"
            type="number"
            min="0"
            step="1"
            value={priority}
            disabled={submitting}
            onChange={(event) => setPriority(event.target.value)}
          />
        </Field>

        <Field>
          <label htmlFor="commercial-rule-configuration">Configuration JSON</label>
          <textarea
            id="commercial-rule-configuration"
            rows="10"
            spellCheck="false"
            value={configurationText}
            disabled={submitting}
            onChange={(event) => setConfigurationText(event.target.value)}
          />
          <small>Le Backend reste la source de vérité métier.</small>
        </Field>

        <Checkbox>
          <input
            id="commercial-rule-active"
            type="checkbox"
            checked={active}
            disabled={submitting}
            onChange={(event) => setActive(event.target.checked)}
          />
          <label htmlFor="commercial-rule-active">Règle active</label>
        </Checkbox>

        {(validationError || errorMessage) && (
          <Error role="alert">{validationError || errorMessage}</Error>
        )}

        <Actions>
          <AdminButton variant="secondary" type="button" disabled={submitting} onClick={onCancel}>Annuler</AdminButton>
          <Primary type="submit" disabled={submitting}>
            {submitting ? "Enregistrement…" : "Enregistrer"}
          </Primary>
        </Actions>
      </Form>
    </Modal>
  );
}

const Form = styled.form`
  width: min(620px, 100%);
  h2 { margin: 0 2.5rem 1.2rem 0; color: #f7f7f7; }
`;

const Field = styled.div`
  display: grid;
  gap: .4rem;
  margin-bottom: 1rem;
  label { color: rgba(247,247,247,.8); font-weight: 750; }
  input, select, textarea {
    width: 100%; min-height: 42px; padding: .7rem .8rem;
    border: 1px solid rgba(255,255,255,.13); border-radius: 10px;
    background: #151d31; color: #f7f7f7; font: inherit;
  }
  textarea { resize: vertical; font-family: monospace; }
  small { color: rgba(247,247,247,.55); }
`;

const Checkbox = styled.div`
  display: flex; align-items: center; gap: .55rem; margin-bottom: 1rem;
`;

const Error = styled.div`
  margin-bottom: 1rem; padding: .7rem .85rem; border-radius: 10px;
  background: rgba(225,95,95,.12); color: #ffb4b4;
`;

const Actions = styled.div`
  display: flex; justify-content: flex-end; gap: .75rem;
`;


const Primary = styled(AdminButton)`
  border: 0; background: #fff9ea; color: #0b1328;
`;
